import _ from 'underscore';
import React, { useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { SelectedItem } from '@bingads-webui-campaign-react/pill-item-components';
import { SearchBox } from '@fluentui/react';
import {
  MaxSelectionAlert,
  DuplicateWordAlert,
} from './alerts';


const useSearchThemeSelectorStyles = makeStyles({
  root: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('10px'),
  },
  description: {
    marginBottom: tokens.spacingVerticalS,
  },
  tagsList: {
    marginBottom: tokens.spacingVerticalXS,
    marginTop: 0,
    paddingLeft: 0,
    display: 'flex',
    gridGap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
  },
  searchBox: {
    flexDirection: 'row',
    columnGap: tokens.spacingHorizontalM,
    width: '100%',
    maxWidth: '600px',
  },
  alertMes: {
    marginTop: '10px',
  },
});

export const SearchThemeSelectorImpl = ({ onUpdateSearchThemes: onChangeFromProp, searchThemes }) => {
  const i18n = useContext(I18nContext);
  const styles = useSearchThemeSelectorStyles();
  const [selectedOptions, setSelectedOptions] = useState(searchThemes);
  const [isLimitReached, setIsLimitReached] = useState(false);
  const [isDuplicate, seIsDuplicate] = useState(false);

  const maxOptionsLen = 25;
  const maxWordLen = 80;

  const onDeleteItem = useCallback((item) => {
    const newSelectedOptions = _.clone(selectedOptions);
    const indexSelected = newSelectedOptions.findIndex(option => option.SearchTheme === item.SearchTheme && !option.Status);
    const draftItem = newSelectedOptions[indexSelected];
    if (draftItem.Id) {
      newSelectedOptions[indexSelected].Status = 'Deleted';
    } else {
      newSelectedOptions.splice(indexSelected, 1);
    }
    if (_.isFunction(onChangeFromProp)) {
      onChangeFromProp(newSelectedOptions);
    }
    if (isLimitReached) {
      setIsLimitReached(false);
    }
    setSelectedOptions(newSelectedOptions);
  }, [selectedOptions, onChangeFromProp, isLimitReached]);

  const [searchValue, updateSearchValue] = useState('');

  return (
    <div className={mergeClasses('search-theme-selector', styles.root)}>
      <span className={mergeClasses('search-theme-selector-description', styles.description)}>{i18n.getString(_TL_('Words or phrases that people search for on Bing (up to 25)'))}</span>

      <div className={mergeClasses('search-theme-selector-selected-items', styles.tagsList)}>
        {selectedOptions.filter(item => item.Status !== 'Deleted').map((item, index) => (
          <SelectedItem
            key={item.Id}
            itemText={item.SearchTheme}
            itemIndex={index}
            onDelete={() => onDeleteItem(item)}
          />
        ))}
      </div>

      <div className={mergeClasses('search-theme-selector-search-box', styles.searchBox)}>
        <SearchBox
          placeholder={i18n.getString(_TL_('Enter search themes'))}
          value={searchValue}
          maxLength={maxWordLen}
          onSearch={(newValue) => {
            const newVal = newValue.replace(/\/|\\/g, '');
            const draftSelectedOptions = selectedOptions.filter(item => item.Status !== 'Deleted');
            if (!newVal) {
              updateSearchValue('');
            } else if (draftSelectedOptions.length >= maxOptionsLen) {
              setIsLimitReached(true);
            } else if (draftSelectedOptions.find(item => item.SearchTheme === newVal)) {
              seIsDuplicate(true);
            } else {
              setIsLimitReached(false);
              seIsDuplicate(false);
              selectedOptions.push({ SearchTheme: newVal });

              if (_.isFunction(onChangeFromProp)) {
                onChangeFromProp(selectedOptions);
              }

              updateSearchValue('');
            }
          }}
          onClear={() => {
            updateSearchValue('');
            seIsDuplicate(false);
            setIsLimitReached(false);
          }}
          onChange={(e, newValue) => {
            const newVal = newValue ? newValue.replace(/\/|\\/g, '') : '';
            updateSearchValue(newVal);
            seIsDuplicate(false);
          }}
        />

        <div className={mergeClasses('search-theme-selector-alert', styles.alertMes)}>
          {isLimitReached && <MaxSelectionAlert selectedOptionsLimit={maxOptionsLen} />}
          {isDuplicate && <DuplicateWordAlert />}
        </div>
      </div>
    </div>
  );
};

SearchThemeSelectorImpl.propTypes = {
  onUpdateSearchThemes: PropTypes.func,
  searchThemes: PropTypes.arrayOf(PropTypes.shape({})),
};

SearchThemeSelectorImpl.defaultProps = {
  onUpdateSearchThemes: undefined,
  searchThemes: [],
};
